import { getSoldOutLabel } from '@app/ducks';
import { getIsDefaultBadge } from '@app/ducks/helpers';
import { cn } from '@app/utils/cn';
import { isDefined } from '@app/utils/is';
import { Badges } from '@typings';
import { isEmpty } from 'ramda';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Eta } from '../Icons';
import { Tooltip } from '../Tooltip';

interface Props {
  badge: Badges.DefaultBadge | Badges.CustomBadge;
  size?: 'small' | 'medium';
}

export const ProductBadge = ({ badge, size }: Props) => {
  const { t } = useTranslation(['common', 'products']);
  const isDefaultBadge = getIsDefaultBadge(badge);
  const soldOutLabel = useSelector(getSoldOutLabel);

  if (isDefaultBadge) {
    const defaultStyles: Record<Badges.DefaultBadge['badgeType'], string> = {
      eta: 'bg-neutral-40 text-white pointer-events-auto',
      new: 'bg-neutral-96 text-neutral-20',
      sale: 'bg-neutral-20 text-white',
      soldOut: 'bg-neutral-80 text-neutral-20',
      variantUnavailable: 'bg-white text-neutral-20',
    };

    const badgeLabels: Record<Badges.DefaultBadge['badgeType'], string> = {
      eta: '',
      new: t('common:new'),
      sale: t('common:sale'),
      soldOut: soldOutLabel,
      variantUnavailable: t('products:variant_unavailable'),
    };

    return (
      <Tooltip content={badge.tooltip}>
        <div
          className={cn(
            'flex w-fit max-w-full items-center gap-2 rounded-large px-3 py-2',
            { 'px-2 py-0.5': size === 'small' },
            defaultStyles[badge.badgeType],
          )}
        >
          {!isEmpty(badgeLabels[badge.badgeType]) && <span className="truncate body-6">{badgeLabels[badge.badgeType]}</span>}
          {badge.badgeType === 'eta' && <Eta className="size-4" />}
        </div>
      </Tooltip>
    );
  }

  const hasName = !isEmpty(badge.name);
  const { image, imagePlacement, backgroundColor, textColor } = badge;
  const badgeImage = isDefined(image) && (
    <img src={image.url} className="size-4 object-contain" width={image.width} height={image.height} alt="" />
  );

  return (
    <div
      className={cn('flex w-fit max-w-full items-center gap-2 rounded-large px-3 py-2', { 'px-2 py-0.5': size === 'small' })}
      style={{ backgroundColor, color: textColor }}
    >
      {imagePlacement === 'before' && badgeImage}
      {hasName && <span className="truncate body-6">{badge.name}</span>}
      {imagePlacement === 'after' && badgeImage}
    </div>
  );
};
